import { useState } from "react";
import { User, Mail, Lock, AlertCircle, CheckCircle, X } from "lucide-react";
import axios, { formatApiError, API } from "@/utils/api";

const RegisterPage = ({ onSwitchToLogin, onClose }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, { name, email, password });
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-modal-overlay" onClick={onClose} data-testid="register-success-overlay">
        <div className="auth-card" onClick={(e) => e.stopPropagation()} data-testid="register-success">
          <button className="auth-modal-close" onClick={onClose} data-testid="register-success-close-btn">
            <X size={20} />
          </button>
          <div className="auth-header">
            <div className="success-icon">
              <CheckCircle size={48} />
            </div>
            <h2>Registration Successful!</h2>
            <p className="text-muted">
              Your account has been created and is pending admin approval.
              You will be notified once your account is approved.
            </p>
          </div>
          <button className="btn btn-primary btn-lg btn-full" onClick={onSwitchToLogin}>
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-modal-overlay" onClick={onClose} data-testid="register-modal-overlay">
      <div className="auth-card" onClick={(e) => e.stopPropagation()} data-testid="register-page">
        <button className="auth-modal-close" onClick={onClose} data-testid="register-close-btn">
          <X size={20} />
        </button>
        <div className="auth-header">
          <div className="auth-logo">
            <img src="/logo.png" alt="CO2e Portal" className="auth-modal-logo-img" />
          </div>
          <h2>Create Account</h2>
          <p className="text-muted">Register as an auditor</p>
        </div>

        {error && (
          <div className="alert alert-danger">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <div className="input-icon-wrapper">
              <User size={18} className="input-icon" />
              <input
                type="text"
                className="form-input with-icon"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email</label>
            <div className="input-icon-wrapper">
              <Mail size={18} className="input-icon" />
              <input
                type="email"
                className="form-input with-icon"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div className="input-icon-wrapper">
              <Lock size={18} className="input-icon" />
              <input
                type="password"
                className="form-input with-icon"
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="register-password"
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-lg btn-full" disabled={loading} data-testid="register-submit">
            {loading ? "Creating account..." : "Create Account"}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{" "}
            <button className="link-button" onClick={onSwitchToLogin} data-testid="switch-to-login">
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
